import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { fetchExportCsv } from '@/api/exports';
import type { ExportType } from '@/api/exports';
import { downloadBlob } from '@/lib/csv';
import type { DateRange } from '@/lib/dateRange';
import { errorMessageOf } from '@/lib/errors';
import { toast } from '@/store/useToastStore';

interface ExportControlsProps {
  /** The `type` query value the export endpoint expects for this screen. */
  type: ExportType;
  /** Prefix for the downloaded file name, e.g. `returns` → `returns-2026-07-01_2026-07-14.csv`. */
  filenamePrefix: string;
  /** The date range to export — shared with the screen's own list query/picker. */
  range: DateRange;
  /** Optional status to scope the export to, matching the screen's active tab. */
  status?: string;
  /** Optional comma-separated statuses (for tabs that span multiple statuses). */
  statuses?: string;
}

/**
 * Server-side CSV export button. Exports the same date range the screen's list
 * query is currently using — the range itself is owned by the screen so both
 * the list fetch and the export stay in sync with a single date-range picker.
 */
export function ExportControls({ type, filenamePrefix, range, status, statuses }: ExportControlsProps) {
  const [isExporting, setIsExporting] = useState(false);

  async function handleExport() {
    setIsExporting(true);
    try {
      const blob = await fetchExportCsv(type, range, { status, statuses });
      downloadBlob(`${filenamePrefix}-${range.from}_${range.to}.csv`, blob);
      toast.success('Export ready');
    } catch (error) {
      toast.error(errorMessageOf(error));
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Button variant="secondary" size="sm" onClick={handleExport} isLoading={isExporting}>
      Export
    </Button>
  );
}
